import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { BrandCard } from "../components/BrandCard";
import { EcosystemOrbit } from "../components/EcosystemOrbit";
import { QuestionnaireTeaser } from "../components/QuestionnaireTeaser";
import { QuestionnaireModal } from "../components/QuestionnaireModal";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Buildings, ChartLineUp, Coins, Stack, GraduationCap, ShieldCheck, MapPin, MagnifyingGlass, UserPlus, Storefront, MagnifyingGlassPlus, FileText, CreditCard, Handshake } from "@phosphor-icons/react";

const stats = [
  { icon: Stack, n: "40+", l: "Brands & growing" },
  { icon: GraduationCap, n: "10", l: "Brands live at launch" },
  { icon: MapPin, n: "Multiple", l: "Sectors" },
  { icon: Coins, n: "₹10K", l: "Minimum contribution" },
];

const why = [
  { icon: Buildings, k: "Owned & Operated", t: "Integrated Ecosystem", d: "Every brand is owned, operated & grown by EBG. Not a marketplace. Not an aggregator." },
  { icon: Coins, k: "Flexible Entry Options", t: "Exclusive Franchise Partner Model", d: "Choose from one or multiple franchise opportunities with flexible participation options." },
  { icon: ChartLineUp, k: "Diverse Opportunities", t: "Diversified Portfolio", d: "Explore franchise opportunities across multiple industries and business categories within the EBG ecosystem." },
];

const CTA_BG = "https://customer-assets.emergentagent.com/job_fincorp-strategy/artifacts/znhsgejm_image05.png";

const journey = [
  { n: "01", t: "Discover", d: "Explore EBG FinCorp and our brands.", icon: MagnifyingGlass },
  { n: "02", t: "Create Account", d: "Sign up and verify your details.", icon: UserPlus },
  { n: "03", t: "Explore Brands", d: "Browse opportunities that interest you.", icon: Storefront },
  { n: "04", t: "Deep Dive", d: "Study brand details, business model and payout structure.", icon: MagnifyingGlassPlus },
  { n: "05", t: "Express Interest", d: "Fill the intent form for your chosen brands.", icon: FileText },
  { n: "06", t: "Pay Intent Fee", d: "Pay the intent fee to continue with your application.", icon: CreditCard },
  { n: "07", t: "Due Diligence", d: "We verify your profile and documents.", icon: ShieldCheck },
  { n: "08", t: "Partnership", d: "Agreement signed. You're now a partner.", icon: Handshake },
];

export default function Home() {
  const [brands, setBrands] = useState([]);
  const [quizOpen, setQuizOpen] = useState(false);
  useEffect(() => { api.get("/brands").then(({ data }) => setBrands(data)).catch(() => {}); }, []);

  return (
    <div data-testid="home-page">
      <QuestionnaireModal open={quizOpen} onClose={() => setQuizOpen(false)} />
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-white/10">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -top-32 -left-20 w-[600px] h-[600px] rounded-full bg-gold/10 blur-[150px]" />
          <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "linear-gradient(rgba(255,255,255,.6) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.6) 1px,transparent 1px)", backgroundSize: "64px 64px" }} />
        </div>
        <div className="relative px-6 md:px-12 lg:px-20 pt-16 pb-12 md:pt-24 md:pb-16 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-gold" /><span className="eyebrow">EBG Group · Exclusive · Franchise Partner</span>
            </motion.div>
            <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
              className="font-display text-5xl md:text-7xl tracking-tight leading-[1.02] text-white">
              EBG Group's<br />Exclusive<br /><span className="gold-text italic">Franchise Partner</span><br />Platform.
            </motion.h1>
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
              className="mt-7 text-base md:text-lg text-ebg-muted max-w-xl leading-relaxed">
              Participate in the growth of India's fastest-growing franchise brands through one integrated ecosystem.
            </motion.p>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.45 }} className="mt-9 flex flex-wrap gap-4">
              <Link to="/register" data-testid="hero-register-btn" className="btn-gold px-7 py-3.5 rounded-md flex items-center gap-2">
                Become an Ecosystem Partner <ArrowRight size={18} />
              </Link>
              <Link to="/explore" data-testid="hero-explore-btn" className="border border-white/20 text-white px-7 py-3.5 rounded-md hover:border-gold hover:text-gold transition-colors">
                Explore Franchises
              </Link>
            </motion.div>
          </div>
          <motion.div initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.2 }}>
            <EcosystemOrbit />
          </motion.div>
        </div>

        {/* stat row */}
        <div className="relative px-6 md:px-12 lg:px-20 pb-14">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 rounded-xl overflow-hidden border border-white/10">
            {stats.map((s) => (
              <div key={s.l} className="bg-navy-950 p-6 flex items-center gap-4">
                <s.icon size={26} className="text-gold flex-shrink-0" weight="light" />
                <div>
                  <p className="font-display text-3xl text-white leading-none">{s.n}</p>
                  <p className="text-[0.65rem] uppercase tracking-widest text-ebg-muted mt-1.5">{s.l}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* QUESTIONNAIRE TEASER */}
      <QuestionnaireTeaser onStart={() => setQuizOpen(true)} />

      {/* WHY */}
      <section className="px-6 md:px-12 lg:px-20 py-20 md:py-28 border-t border-white/10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          {/* sticky intro */}
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-24">
              <p className="eyebrow mb-5">Why EBG FinCorp?</p>
              <h2 className="font-display text-4xl md:text-6xl text-white leading-[1.04]">A different way to own.<br /><span className="gold-text italic">A better way to grow.</span></h2>
              <p className="text-ebg-muted mt-6 text-lg leading-relaxed max-w-md">
                One integrated ecosystem of owned-and-operated brands—created for those looking to participate in a diverse range of franchise opportunities through a single platform.
              </p>
              <Link to="/explore" className="group inline-flex items-center gap-2 mt-8 text-gold font-medium">
                Explore the ecosystem
                <span className="grid place-items-center w-8 h-8 rounded-full border border-gold/40 group-hover:bg-gold group-hover:text-navy-950 transition-colors"><ArrowRight size={15} /></span>
              </Link>
            </div>
          </div>

          {/* feature cards */}
          <div className="lg:col-span-7 flex flex-col gap-5">
            {why.map((w, i) => (
              <motion.div key={w.t}
                initial={{ opacity: 0, y: 22 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.12, duration: 0.5 }}
                className="group relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.05] to-transparent p-7 md:p-8 transition-all duration-300 hover:border-gold/40">
                {/* accent bar */}
                <span className="absolute left-0 top-0 h-full w-[3px] bg-gradient-to-b from-gold to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                {/* ghost index */}
                <span className="absolute -right-3 -top-5 font-display text-[7rem] leading-none text-white/[0.04] group-hover:text-gold/10 transition-colors duration-300 select-none">0{i + 1}</span>
                <div className="relative flex items-start gap-5 md:gap-6">
                  <div className="grid place-items-center w-14 h-14 rounded-xl gold-gradient text-navy-950 shadow-[0_8px_30px_-8px_rgba(212,175,55,0.5)] transition-transform duration-300 group-hover:-translate-y-0.5 flex-shrink-0">
                    <w.icon size={26} weight="bold" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[0.62rem] uppercase tracking-[0.22em] text-gold/80 mb-2">{w.k}</p>
                    <h3 className="font-display text-2xl md:text-3xl text-white leading-none">{w.t}</h3>
                    <p className="text-sm md:text-[0.95rem] text-ebg-muted mt-3 leading-relaxed max-w-lg">{w.d}</p>
                  </div>
                  <ArrowUpRight size={22} className="text-ebg-muted group-hover:text-gold transition-all duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 flex-shrink-0" />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* BRANDS */}
      <section className="px-6 md:px-12 lg:px-20 py-16 border-t border-white/10">
        <div className="flex items-end justify-between mb-12">
          <div>
            <p className="eyebrow mb-3">The Brands</p>
            <h2 className="font-display text-4xl md:text-5xl text-white">The ten going live first.</h2>
          </div>
          <Link to="/explore" className="hidden md:flex items-center gap-2 text-gold text-sm hover:gap-3 transition-all">View all <ArrowRight size={16} /></Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {brands.slice(0, 6).map((b) => <BrandCard key={b.slug} brand={b} />)}
        </div>
      </section>

      {/* JOURNEY */}
      <section className="px-6 md:px-12 lg:px-20 py-20 md:py-28 border-t border-white/10">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-16">
          <div>
            <p className="eyebrow mb-4">How It Works</p>
            <h2 className="font-display text-4xl md:text-6xl text-white leading-[1.04]">Your franchise journey,<br /><span className="gold-text italic">in eight steps.</span></h2>
          </div>
          <p className="text-ebg-muted text-lg max-w-sm lg:text-right">From your first visit to a signed franchise agreement — a guided, transparent path, every step of the way.</p>
        </div>

        <div className="space-y-5">
          {[journey.slice(0, 4), journey.slice(4)].map((row, ri) => (
            <div key={ri} className="relative grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {/* connector line linking the step icons */}
              <div className="hidden lg:block absolute left-10 right-10 top-[48px] h-px bg-gradient-to-r from-transparent via-gold/30 to-transparent" />
              {row.map((s, i) => (
                <motion.div key={s.n}
                  initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08, duration: 0.5 }}
                  className="group relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-b from-white/[0.05] to-transparent p-6 transition-all duration-300 hover:border-gold/40 hover:-translate-y-1.5">
                  <span className="absolute right-5 top-4 font-display text-4xl text-white/[0.08] group-hover:text-gold/30 transition-colors duration-300 select-none">{s.n}</span>
                  <div className="relative grid place-items-center w-12 h-12 rounded-xl gold-gradient text-navy-950 shadow-[0_8px_30px_-8px_rgba(212,175,55,0.5)] transition-transform duration-300 group-hover:-translate-y-0.5">
                    <s.icon size={24} weight="bold" />
                  </div>
                  <h3 className="font-display text-2xl text-white mt-5">{s.t}</h3>
                  <p className="text-sm text-ebg-muted mt-2 leading-relaxed">{s.d}</p>
                  <span className="absolute left-0 bottom-0 h-[3px] w-0 group-hover:w-full bg-gradient-to-r from-gold to-transparent transition-all duration-500" />
                </motion.div>
              ))}
            </div>
          ))}
        </div>

        <div className="text-center mt-12">
          <Link to="/how-it-works" className="group inline-flex items-center gap-2 text-gold font-medium">
            See the full journey
            <span className="grid place-items-center w-8 h-8 rounded-full border border-gold/40 group-hover:bg-gold group-hover:text-navy-950 transition-colors"><ArrowRight size={15} /></span>
          </Link>
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 md:px-12 lg:px-20 py-20 md:py-24 border-t border-white/10">
        <div className="relative overflow-hidden rounded-2xl border border-gold/20 min-h-[340px] flex items-center justify-center">
          <img src={CTA_BG} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-navy-950/70" />
          <div className="absolute inset-0 bg-gradient-to-r from-navy-950 via-navy-950/55 to-navy-950/20" />
          <div className="relative text-center px-6 py-16 md:py-20 max-w-3xl mx-auto">
            <ShieldCheck size={36} className="text-gold mx-auto" weight="light" />
            <h2 className="font-display text-3xl sm:text-4xl md:text-6xl text-white tracking-tight mt-6 leading-tight">
              Become an <span className="gold-text">Ecosystem Partner.</span>
            </h2>
            <p className="text-ebg-muted mt-5 max-w-xl mx-auto">A small, refundable intent fee locks your slot. Everything else, we handle with you.</p>
            <Link to="/register" className="inline-flex items-center gap-2 mt-9 btn-gold px-8 py-4 rounded-md">Get Started <ArrowRight size={18} /></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
